"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useToggletip = void 0;
var _web = require("@react-hookz/web");
var _designTokens = require("@smartsheet/design-tokens");
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _react = require("react");
var _StackOrder = require("../../util/StackOrder");
var _flyout = require("../flyout");
var _useDelayedOpen = require("../flyout/hooks/useDelayedOpen");
var _focusMode = require("../focusMode");
/**
 * This thing is in charge of hooking up the Toggletip to its target.
 */
const useToggletip = function () {
  let {
    showOnHover,
    showOnHoverCloseDelay = 300,
    ...rest
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const {
    focusMode,
    setFocusMode
  } = (0, _focusMode.useFocusMode)();
  const focusModeIsKeyboard = focusMode === "keyboard";
  /**
   * If the user is using the keyboard to open the tooltip and to navigate within the tooltip, then the X close button needs to show
   */
  const shouldRemoveCloseButton = showOnHover && !focusModeIsKeyboard;
  const localTargetRef = (0, _react.useRef)(null);
  const combinedTargetRef = (0, _lodestarUtils.useForkRef)(localTargetRef, rest.targetRef || null);
  const {
    isOpen,
    setIsOpen,
    cancelExitDelayTimer
  } = (0, _useDelayedOpen.useDelayedOpen)({
    /**
     * If we are using the keyboard, we don't want there to be any
     * delay. So, we set the delay to 0ms for both closeDelay and enterDelay
     * */
    exitDelay: showOnHover && !focusModeIsKeyboard ? showOnHoverCloseDelay : 0,
    enterDelay: showOnHover && !focusModeIsKeyboard ? 500 : 0
  });
  const {
    flyoutProps,
    targetProps: {
      "aria-owns": _0,
      ...restTargetProps
    }
  } = (0, _flyout.useFlyout)({
    isOpen: isOpen,
    onCloseRequested: () => setIsOpen(false),
    targetRef: combinedTargetRef,
    ...rest
  });
  const previousIsOpen = (0, _web.usePrevious)(isOpen);
  (0, _react.useEffect)(() => {
    // We only want to focus the target when the Toggletip is closing. We don't want to focus it
    // when it initially mounts.
    if (previousIsOpen && !isOpen) {
      // It's actually important that this runs in an effect since it needs to happen a tick
      // or so later. This is likely due to the tooltip out animation.
      localTargetRef.current?.focus();
    }
  }, [isOpen, previousIsOpen]);
  (0, _StackOrder.useStackOrder)({
    isOpen,
    onCloseRequested: () => setIsOpen(false)
  });
  const getToggletipProps = (0, _react.useCallback)(function () {
    let {
      ...rest
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    const base = {
      portalProps: {
        zIndex: _designTokens.layers.tooltip,
        ...rest.portalProps
      },
      targetRef: localTargetRef,
      ignoreStackOrder: true,
      ...flyoutProps
    };
    if (showOnHover) {
      return {
        ...base,
        onMouseEnter: () => {
          /**
           * To prevent the tooltip from reappearing after briefly moving away and back, we cancel the closeDelay timer instead of triggering onMouseEnter. This resolves the flickering issue by ensuring the tooltip stays hidden during quick mouse movements.
           */
          cancelExitDelayTimer();
        },
        onMouseLeave: () => {
          setFocusMode("mouse");
          setIsOpen(false);
        },
        removeCloseButton: shouldRemoveCloseButton,
        onCloseRequested: () => {
          flyoutProps.onCloseRequested();
          setIsOpen(false);
        }
      };
    } else {
      return base;
    }
  }, [flyoutProps, showOnHover, cancelExitDelayTimer, shouldRemoveCloseButton, setFocusMode, setIsOpen]);
  const getTargetProps = (0, _react.useCallback)(function () {
    let {
      ...overrides
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    if (showOnHover) {
      return {
        ...overrides,
        onClick: function () {
          if (isOpen) {
            setIsOpen(false);
          } else {
            setIsOpen(true);
          }
          for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
            args[_key] = arguments[_key];
          }
          overrides.onClick?.(...args);
        },
        onMouseEnter: function () {
          setFocusMode("mouse");
          setIsOpen(true);
          for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
            args[_key2] = arguments[_key2];
          }
          overrides.onMouseEnter?.(...args);
        },
        onMouseLeave: function () {
          setIsOpen(false);
          for (var _len3 = arguments.length, args = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
            args[_key3] = arguments[_key3];
          }
          overrides.onMouseLeave?.(...args);
        },
        ...restTargetProps
      };
    } else {
      return {
        ...overrides,
        onClick: function () {
          if (isOpen) {
            setIsOpen(false);
          } else {
            setIsOpen(true);
          }
          for (var _len4 = arguments.length, args = new Array(_len4), _key4 = 0; _key4 < _len4; _key4++) {
            args[_key4] = arguments[_key4];
          }
          overrides.onClick?.(...args);
        },
        ...restTargetProps
      };
    }
  }, [isOpen, restTargetProps, showOnHover, setIsOpen, setFocusMode]);
  return {
    getTargetProps,
    getToggletipProps
  };
};
exports.useToggletip = useToggletip;
//# sourceMappingURL=useToggletip.js.map